import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';

const SECURE_KEYS = [
  'bm_access_token',
  'bm_refresh_token',
];

const STANDARD_KEYS = [
  'bm_token_expiry',
  'bm_user',
  'bm_me',
  'bm_guest',
  'bm_push_registration',
];

const ALL_KEYS = [...SECURE_KEYS, ...STANDARD_KEYS];

/**
 * Hybrid storage service.
 * - Native (iOS/Android): stores auth tokens in Keychain/Keystore and keeps
 *   non-sensitive app state in Capacitor Preferences.
 * - Web / ionic serve: falls back to localStorage so dev flow is unchanged.
 *
 * Exposes synchronous get/set/remove by keeping an in-memory cache.
 * Must be initialised via init() (called via APP_INITIALIZER) before any
 * synchronous getters are used.
 */
class StorageService {
  private readonly cache = new Map<string, string>();
  private readonly secureKeys = new Set(SECURE_KEYS);

  /** Loads all persisted values into memory. Must complete before any navigation. */
  async init(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      // Web: mirror existing localStorage entries into the cache.
      for (const key of ALL_KEYS) {
        const val = localStorage.getItem(key);
        if (val != null) this.cache.set(key, val);
      }
      return;
    }

    await SecureStorage.setKeyPrefix('bevme_');
    await SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly);

    // Native: load sensitive token values from Keychain/Keystore.
    await Promise.all(
      SECURE_KEYS.map(async key => {
        const value = await SecureStorage.getItem(key);
        if (value != null) this.cache.set(key, value);
      }),
    );

    // Native: load non-sensitive values from Capacitor Preferences.
    await Promise.all(
      STANDARD_KEYS.map(async key => {
        const { value } = await Preferences.get({ key });
        if (value != null) this.cache.set(key, value);
      }),
    );
  }

  getItem(key: string): string | null {
    return this.cache.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.cache.set(key, value);

    if (Capacitor.isNativePlatform()) {
      if (this.secureKeys.has(key)) {
        void SecureStorage.setItem(key, value);
      } else {
        void Preferences.set({ key, value });
      }
      return;
    }

    try { localStorage.setItem(key, value); } catch { /* storage full */ }
  }

  removeItem(key: string): void {
    this.cache.delete(key);

    if (Capacitor.isNativePlatform()) {
      if (this.secureKeys.has(key)) {
        void SecureStorage.removeItem(key);
      } else {
        void Preferences.remove({ key });
      }
      return;
    }

    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
}

export const storageService = new StorageService();
